import { authPathWithReturn, safeReturnPath } from '@beatq/shared';
import {
  ACCOUNT_ID_STORAGE_KEY,
  SETUP_CHECKLIST_STEPS,
  SIGNUP_SETUP_POLL_MS,
  SIGNUP_SETUP_ROUTES,
  VERIFICATION_REQUIRED_EVIDENCE_TYPES,
} from './catalog.js';
import type {
  BusinessAccount,
  SetupChecklistStep,
  VerificationEvidenceItem,
} from './signupSetupApi.js';

export function corporateSignUpPath(): string {
  return SIGNUP_SETUP_ROUTES['SCR-BIZ-001'];
}

export function corporateSignInPath(returnTo?: string | null): string {
  return authPathWithReturn('/corporate-sign-in', returnTo);
}

export function accountVerificationPath(): string {
  return SIGNUP_SETUP_ROUTES['SCR-BIZ-002'];
}

export function setupChecklistPath(): string {
  return SIGNUP_SETUP_ROUTES['SCR-BIZ-003'];
}

export function accountOverviewPath(): string {
  return SIGNUP_SETUP_ROUTES['SCR-BIZ-004'];
}

const AUTH_LOOP_PREFIXES = ['/corporate-sign-in', '/corporate-sign-up'];

/** Post-auth landing. Preserve approval/invoice deep links; default `/` for B01 landing. */
export function resolvePostAuthPath(raw: string | null | undefined): string {
  const safe = safeReturnPath(raw, '/');
  if (AUTH_LOOP_PREFIXES.some((prefix) => safe === prefix || safe.startsWith(`${prefix}?`))) {
    return '/';
  }
  return safe;
}

export function getStoredAccountId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(ACCOUNT_ID_STORAGE_KEY);
}

export function setStoredAccountId(accountId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(ACCOUNT_ID_STORAGE_KEY, accountId);
}

export function accountLabel(account: {
  legalName?: string | null;
  tradingName?: string | null;
  id?: string;
}): string {
  return account.tradingName ?? account.legalName ?? account.id ?? 'Business Account';
}

export function isChecklistStepComplete(step: { status?: string | null }): boolean {
  const status = (step.status ?? '').toLowerCase();
  return status === 'complete' || status === 'completed' || status === 'done';
}

/** Subtitle helper — never repeats title + status (badge owns status). */
export function checklistStepLabel(step: {
  label: string;
  status?: string | null;
  blockers?: string[] | null;
  optional?: boolean;
}): string {
  if (step.blockers?.length) return step.blockers.join(' · ');
  if (step.optional && !isChecklistStepComplete(step)) {
    return 'Optional — a flat account is valid';
  }
  const status = (step.status ?? '').toLowerCase();
  if (status === 'complete' || status === 'completed') return 'Ready';
  if (status === 'ready') return 'Ready — employees can order after you go live';
  if (status === 'pending' || status === 'in_progress') return 'Action needed';
  return 'Continue setup';
}

export function humanizeToken(value: string | null | undefined): string {
  const raw = (value ?? '').replace(/_/g, ' ').trim().toLowerCase();
  if (!raw) return '';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

export function isVerificationTerminal(state?: string | null): boolean {
  const s = (state ?? '').toUpperCase();
  return ['VERIFIED', 'APPROVED', 'DECLINED', 'REJECTED', 'FULL'].includes(s);
}

/** Evidence submitted or review running — setup may continue (07-09). */
export function isVerificationSatisfied(state?: string | null): boolean {
  const s = (state ?? '').toUpperCase();
  return (
    isVerificationTerminal(state) ||
    s === 'PENDING_REVIEW' ||
    s === 'SUBMITTED' ||
    s === 'IN_REVIEW'
  );
}

export function verificationPollInterval(state?: string | null): number | false {
  return isVerificationTerminal(state) ? false : SIGNUP_SETUP_POLL_MS;
}

/** 10-06 account states that are still setup, not live. Matches B01 landing. */
const NOT_LIVE_ACCOUNT_STATUSES = new Set(['PROSPECT', 'PENDING_SETUP']);

export function isAccountLive(
  account?: {
    status?: string | null;
    state?: string | null;
    onboardedAt?: string | null;
    setupProgress?: number | null;
  } | null,
): boolean {
  if (!account) return false;
  if (account.onboardedAt) return true;
  const s = (account.status ?? account.state ?? '').trim().toUpperCase();
  if (NOT_LIVE_ACCOUNT_STATUSES.has(s)) return false;
  if (s) return true;
  const progress = account.setupProgress;
  if (typeof progress === 'number') return progress >= 100;
  return false;
}

export function catalogStepByCode(code: string) {
  return SETUP_CHECKLIST_STEPS.find((step) => step.code === code);
}

export function resolveChecklistSteps(
  apiSteps: SetupChecklistStep[] | undefined,
  account?: BusinessAccount | null,
): SetupChecklistStep[] {
  if (apiSteps && apiSteps.length > 0) {
    return apiSteps.map((step) => {
      const catalog = catalogStepByCode(step.code);
      return {
        ...step,
        label: step.label || catalog?.label || step.code,
        route: step.route ?? catalog?.route,
        optional: Boolean(catalog?.optional) || Boolean(step.optional),
      };
    });
  }

  return SETUP_CHECKLIST_STEPS.map((step) => {
    if (step.code === 'account-verification') {
      const done = isVerificationSatisfied(account?.verificationState);
      return {
        code: step.code,
        label: step.label,
        route: step.route,
        screenId: step.screenId,
        optional: step.optional,
        status: done ? 'complete' : 'pending',
        blockers: done ? [] : ['Submit required organisational evidence'],
      };
    }
    if (step.code === 'billing-contact') {
      const done = Boolean(account?.billingEmail);
      return {
        code: step.code,
        label: step.label,
        route: step.route,
        screenId: step.screenId,
        optional: step.optional,
        status: done ? 'complete' : 'pending',
        blockers: done ? [] : ['Set a billing email'],
      };
    }
    return {
      code: step.code,
      label: step.label,
      route: step.route,
      screenId: step.screenId,
      optional: step.optional,
      status: 'pending',
      blockers: [],
    };
  });
}

export function checklistProgressPercent(
  steps: SetupChecklistStep[],
  reported?: number | null,
): number {
  if (typeof reported === 'number' && Number.isFinite(reported)) {
    return Math.max(0, Math.min(100, Math.round(reported)));
  }
  if (!steps.length) return 0;
  const done = steps.filter(isChecklistStepComplete).length;
  return Math.round((done / steps.length) * 100);
}

export function isOptionalChecklistStep(step: {
  optional?: boolean;
  status?: string | null;
}): boolean {
  if (step.optional) return true;
  return (step.status ?? '').toLowerCase() === 'optional';
}

export function isGoLiveStep(step: { code?: string | null }): boolean {
  return (step.code ?? '').toLowerCase() === 'go-live';
}

export function isGoLiveReady(
  steps: SetupChecklistStep[],
  account?: BusinessAccount | null,
  reported?: boolean,
): boolean {
  if (isAccountLive(account)) return false;
  if (namedBlockers(steps).length > 0) return false;
  if (reported === true) return true;
  const goLive = steps.find(isGoLiveStep);
  if (goLive) return (goLive.status ?? '').toLowerCase() === 'ready';
  return true;
}

export function namedBlockers(steps: SetupChecklistStep[]): string[] {
  const blockers: string[] = [];
  for (const step of steps) {
    if (isChecklistStepComplete(step) || isOptionalChecklistStep(step)) continue;
    for (const blocker of step.blockers ?? []) {
      const text = blocker.trim();
      if (text && !blockers.includes(text)) blockers.push(text);
    }
  }
  return blockers;
}

export function evidenceStatusForType(
  items: VerificationEvidenceItem[] | undefined,
  evidenceType: string,
): VerificationEvidenceItem | undefined {
  return (items ?? []).find((item) => item.evidenceType === evidenceType);
}

export function requiredEvidenceTypes(fromApi?: string[]): string[] {
  if (fromApi && fromApi.length > 0) return fromApi;
  return [...VERIFICATION_REQUIRED_EVIDENCE_TYPES];
}

export function spendToDateMinor(
  account?: BusinessAccount | null,
  fallbackSum?: number | null,
): number | null {
  if (account?.spendToDateMinor != null) return account.spendToDateMinor;
  if (account?.creditUsedMinor != null) return account.creditUsedMinor;
  if (account?.creditUsed != null) return account.creditUsed;
  if (fallbackSum != null) return fallbackSum;
  return null;
}

export function budgetRemainingMinor(
  account?: BusinessAccount | null,
  fallbackSum?: number | null,
): number | null {
  if (account?.budgetRemainingMinor != null) return account.budgetRemainingMinor;
  const spend = spendToDateMinor(account);
  if (account?.creditLimitMinor != null && spend != null) {
    return account.creditLimitMinor - spend;
  }
  if (fallbackSum != null) return fallbackSum;
  return null;
}

export function invoiceStateLabel(
  account?: BusinessAccount | null,
  invoices?: Array<{ state?: string | null; status?: string | null }>,
): string {
  if (account?.invoiceState) return account.invoiceState;
  const latest = invoices?.[0];
  const fromInvoice = latest?.state ?? latest?.status;
  if (fromInvoice) return fromInvoice;
  return 'None issued';
}

const PENDING_APPROVAL_STATES = new Set([
  'PENDING',
  'OPEN',
  'AWAITING',
  'IN_REVIEW',
  'SUBMITTED',
  'REQUESTED',
]);

export function isPendingApproval(item: {
  status?: string | null;
  state?: string | null;
}): boolean {
  const s = (item.status ?? item.state ?? '').toUpperCase();
  if (!s) return true;
  return PENDING_APPROVAL_STATES.has(s);
}

export function asRecord(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function overviewApprovalFromUnknown(value: unknown): {
  id?: string;
  summary?: string;
  status?: string;
  amountMinor?: number;
  requesterName?: string;
  expiresAt?: string;
} {
  const row = asRecord(value) ?? {};
  const amount = row.amountMinor ?? asRecord(row.subjectSummary)?.amountMinor;
  const expiresAt =
    (typeof row.expiresAt === 'string' && row.expiresAt) ||
    (typeof row.expiry === 'string' && row.expiry) ||
    undefined;
  return {
    id: typeof row.id === 'string' ? row.id : undefined,
    summary:
      (typeof row.summary === 'string' && row.summary) ||
      (typeof row.justification === 'string' && row.justification) ||
      (typeof row.requesterName === 'string' && row.requesterName) ||
      'Approval request',
    status:
      (typeof row.status === 'string' && row.status) ||
      (typeof row.state === 'string' && row.state) ||
      'PENDING',
    amountMinor: typeof amount === 'number' ? amount : undefined,
    requesterName: typeof row.requesterName === 'string' ? row.requesterName : undefined,
    expiresAt,
  };
}

/** Daily-glance window for 07-09 “approval about to expire”. */
export const OVERVIEW_EXPIRY_WINDOW_MS = 60 * 60 * 1000;

export function isApprovalExpiringSoon(
  expiresAt?: string | null,
  now = Date.now(),
  windowMs = OVERVIEW_EXPIRY_WINDOW_MS,
): boolean {
  if (!expiresAt) return false;
  const remaining = Date.parse(expiresAt) - now;
  return Number.isFinite(remaining) && remaining > 0 && remaining <= windowMs;
}

export function approvalExpiryLabel(expiresAt?: string | null, now = Date.now()): string | null {
  if (!expiresAt) return null;
  const remaining = Date.parse(expiresAt) - now;
  if (!Number.isFinite(remaining)) return null;
  if (remaining <= 0) return 'Expired';
  const minutes = Math.floor(remaining / 60_000);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    return `${hours}h left`;
  }
  if (minutes < 1) return 'Less than a minute left';
  return `${minutes} min left`;
}

export function isOverdueInvoice(item: { state?: string | null; status?: string | null }): boolean {
  return (item.state ?? item.status ?? '').toUpperCase() === 'OVERDUE';
}

export type OverviewUnusualSpend = {
  label: string;
  percentAhead: number | null;
};

/** Prefer budget-row signals; fill from GET /account so at-risk is never a stand-in. */
export function mergeUnusualSpendSignals(
  fromBudgets: OverviewUnusualSpend[],
  fromAccount: OverviewUnusualSpend[],
): OverviewUnusualSpend[] {
  const byLabel = new Map<string, OverviewUnusualSpend>();
  for (const item of [...fromBudgets, ...fromAccount]) {
    const key = item.label.trim().toLowerCase() || 'a department';
    const existing = byLabel.get(key);
    if (!existing) {
      byLabel.set(key, item);
    } else if (existing.percentAhead == null && item.percentAhead != null) {
      byLabel.set(key, item);
    }
  }
  return [...byLabel.values()];
}

export function isGoLivePermissionWritable(perm: string): boolean {
  return perm === 'editable';
}

/** 07-09: “Litigation is 12 % ahead of last month” — not the at-risk threshold sentence. */
export function unusualSpendSentence(item: OverviewUnusualSpend): string {
  const label = item.label.trim() || 'A department';
  if (item.percentAhead != null && Number.isFinite(item.percentAhead) && item.percentAhead > 0) {
    return `${label} is ${item.percentAhead}% ahead of last month.`;
  }
  return `${label} has an unusual spend pattern.`;
}

export function overviewExceptionSentences(input: {
  atRiskBudgetLabels: string[];
  unusualSpend?: OverviewUnusualSpend[];
  expiringApprovalCount: number;
  overdueInvoiceCount: number;
}): string[] {
  const sentences: string[] = [];
  if (input.atRiskBudgetLabels.length === 1) {
    const label = input.atRiskBudgetLabels[0] ?? 'A department';
    sentences.push(`${label} is over its budget alert threshold.`);
  } else if (input.atRiskBudgetLabels.length > 1) {
    sentences.push(
      `${input.atRiskBudgetLabels.length} departments are over their budget alert thresholds.`,
    );
  }
  for (const item of input.unusualSpend ?? []) {
    sentences.push(unusualSpendSentence(item));
  }
  if (input.expiringApprovalCount === 1) {
    sentences.push('An approval is about to expire.');
  } else if (input.expiringApprovalCount > 1) {
    sentences.push(`${input.expiringApprovalCount} approvals are about to expire.`);
  }
  if (input.overdueInvoiceCount === 1) {
    sentences.push('An invoice is overdue.');
  } else if (input.overdueInvoiceCount > 1) {
    sentences.push(`${input.overdueInvoiceCount} invoices are overdue.`);
  }
  return sentences;
}

export function verificationBannerKind(
  state?: string | null,
): 'review' | 'declined' | 'verified' | null {
  const s = (state ?? '').toUpperCase();
  if (['DECLINED', 'REJECTED'].includes(s)) return 'declined';
  if (['VERIFIED', 'APPROVED', 'FULL'].includes(s)) return 'verified';
  if (['PENDING_REVIEW', 'SUBMITTED', 'IN_REVIEW'].includes(s)) return 'review';
  return null;
}

export function overviewInvoiceFromUnknown(value: unknown): {
  id?: string;
  number?: string;
  status?: string;
  state?: string;
  totalMinor?: number;
} {
  const row = asRecord(value) ?? {};
  const number =
    (typeof row.number === 'string' && row.number) ||
    (row.invoiceNumber != null ? String(row.invoiceNumber) : undefined);
  return {
    id: typeof row.id === 'string' ? row.id : undefined,
    number,
    status: typeof row.status === 'string' ? row.status : undefined,
    state: typeof row.state === 'string' ? row.state : undefined,
    totalMinor: typeof row.totalMinor === 'number' ? row.totalMinor : undefined,
  };
}
